import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Card from "@mui/material/Card";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import useApi from "components/Utils/api";
import toast from "react-hot-toast";
import RouteAccountsTab from "./tabs/RouteAccountsTab";
import ProviderConfigsTab from "./tabs/ProviderConfigsTab";
import MerchantAssignmentsTab from "./tabs/MerchantAssignmentsTab";

const statusColor = { ACTIVE: "success", INACTIVE: "default", SUSPENDED: "error" };

export default function SettlementRouteDetail() {
  const api = useApi();
  const navigate = useNavigate();
  const { id } = useParams();
  const [route, setRoute] = useState(null);
  const [tab, setTab] = useState(0);

  const load = () => {
    api.get(`settlement-routes/${id}`).then((res) => setRoute(res.data)).catch(() => toast.error("Failed to load route"));
  };

  useEffect(() => { load(); }, [id]);

  if (!route) return null;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <MDBox mb={2} display="flex" alignItems="center" gap={2}>
          <MDButton variant="outlined" color="secondary" size="small" onClick={() => navigate("/settlement-routes")}>
            <Icon>arrow_back</Icon>&nbsp;Back
          </MDButton>
          <MDTypography variant="h5">{route.name}</MDTypography>
          <Chip label={route.status} color={statusColor[route.status] || "default"} size="small" />
          {route.isDefault && <Chip label="Default" color="primary" size="small" />}
          {route.settlementMode === "EXTERNAL" && (
            <Chip label="EXTERNAL SETTLEMENT" color="warning" size="small" />
          )}
        </MDBox>

        <Card>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, pt: 1 }}>
            <Tab label="Route Info" icon={<Icon>info</Icon>} iconPosition="start" />
            <Tab label="Accounts" icon={<Icon>account_balance</Icon>} iconPosition="start" />
            <Tab label="Provider Configs" icon={<Icon>settings</Icon>} iconPosition="start" />
            <Tab label="Merchant Assignments" icon={<Icon>store</Icon>} iconPosition="start" />
          </Tabs>

          <MDBox p={3}>
            {tab === 0 && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <MDTypography variant="caption" color="text">Bank</MDTypography>
                  <MDTypography variant="body2">{route.bankName || "—"} ({route.bankCode || "—"})</MDTypography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <MDTypography variant="caption" color="text">Mode</MDTypography>
                  <MDTypography variant="body2">{route.mode}</MDTypography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <MDTypography variant="caption" color="text">Settlement Mode</MDTypography>
                  <MDTypography variant="body2">{route.settlementMode || "INTERNAL"}</MDTypography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <MDTypography variant="caption" color="text">Created</MDTypography>
                  <MDTypography variant="body2">{route.createdAt ? new Date(route.createdAt).toLocaleDateString() : "—"}</MDTypography>
                </Grid>
                <Grid item xs={12} mt={2}>
                  <MDButton
                    variant="gradient"
                    color="info"
                    size="small"
                    startIcon={<Icon>edit</Icon>}
                    onClick={() => navigate(`/settlement-routes/${id}/edit`)}
                  >
                    Edit Route
                  </MDButton>
                </Grid>
              </Grid>
            )}
            {tab === 1 && (
              route.settlementMode === "EXTERNAL" ? (
                <MDBox p={2}>
                  <MDTypography variant="body2" color="text">
                    This route uses <strong>external settlement</strong> — the PSP settles directly to merchants.
                    No nodal accounts are required or used for settlement.
                  </MDTypography>
                </MDBox>
              ) : (
                <RouteAccountsTab routeId={id} />
              )
            )}
            {tab === 2 && <ProviderConfigsTab routeId={id} />}
            {tab === 3 && <MerchantAssignmentsTab routeId={id} />}
          </MDBox>
        </Card>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}
